// Modules
import moment from "moment";
import { Button, DatePickerProps, Form } from "antd";

// Hooks
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// components
import Modal from "components/molecules/ModalContainer/ModalContainer";
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// Styles
import styles from "containers/ReviewRetrospectiveClaim/reviewRetrospectiveClaim.module.css";

// Constants & Utils
import { Routes, REVIEW_RETRO_CLAIM, REVIEW_RETRO_CLAIM_FORM as Constants, DateTimeFormats } from "constants/global";
import { validateReviewClaim } from "containers/ReviewRetrospectiveClaim/reviewClaimValidation";
import { downloadFile } from "utils/downloadFile";
import getIcon from "utils/getIcon";
import { formatPaymentModeForApi, normalizePaymentMode } from "utils/paymentMode";
import { validateAmount, validateCardNumber, validateClaimReceiptNumber } from "utils/Validations/fieldValidations";

// Redux
import { getConfigData, getConfigDataLoading, getLoggedInUserInfo, getStoreList, getUserPermissionListSelector } from "redux/userList/userSlice";
import { editRetroClaim, retroClaimInfoUpdater, getLoadingStateSelector, getRetroClaimPermissions, getClaimPermissionsSelector } from "redux/retroClaim/retroClaimSlice";

// Type Definitions
import type { RetroClaimType, Users, ClaimDataType } from "redux/types";
import type { OutletItem } from "services/api/types";

export type ReviewClaimDetailsType = {
	status: string;
	source: string;
	payment_mode: string;
	submission_datetime: string;
	username: string;
	agent_id: string;
	receipt_number: string;
	receipt_datetime: string;
	cr_card_number: string;
	purchase_amount: string;
	net_amount: string;
	receipt_imgurl: string;
};

export type ReviewClaimFieldErrors = {
	receipt_number?: boolean;
	cr_card_number?: boolean;
	purchase_amount?: boolean;
	net_amount?: boolean;
};

export type FilledDataValidationType = {
	receipt_datetime?: boolean;
	net_amount?: boolean;
};

export type DisabledFieldsType = {
	receipt_number?: boolean;
	receipt_datetime?: boolean;
	cr_card_number?: boolean;
	outlet_code?: boolean;
	purchase_amount?: boolean;
	net_amount?: boolean;
	payment_mode?: boolean;
	receipt_imgurl?: boolean;
	approve_btn?: boolean;
	reject_btn?: boolean;
	spl_approve?: boolean;
};

export type LocationState = {
	state: {
		claimInfo: ClaimDataType;
	};
};

const initialErrorState = {
	receipt_number: false,
	cr_card_number: false,
	purchase_amount: false,
	net_amount: false
};

const fieldsToValidate = [
	{ key: "receipt_number", validator: validateClaimReceiptNumber },
	{ key: "cr_card_number", validator: validateCardNumber },
	{ key: "purchase_amount", validator: validateAmount },
	{ key: "net_amount", validator: validateAmount }
];

const getFilename = (pathname: string) => {
	return pathname.substring(pathname.lastIndexOf("/") + 1);
};

export function useReviewRetrospectiveClaim() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [form] = Form.useForm();

	const dataLoading = useAppSelector(getLoadingStateSelector);
	const configDataLoading = useAppSelector(getConfigDataLoading);
	const userPermissionList = useAppSelector(getUserPermissionListSelector);
	const claimPermissions = useAppSelector(getClaimPermissionsSelector);

	const location = useLocation();
	const { state } = location as LocationState;

	const { claimInfo } = state;
	const initialPaymentMode = normalizePaymentMode(claimInfo?.payment_mode);

	const userInfo: Users["user"] = useAppSelector(getLoggedInUserInfo);
	const { username, user_id } = userInfo?.data?.[0] ?? {};

	// Track if the user has made edits to the form
	const [hasUserMadeEdits, setHasUserMadeEdits] = useState<boolean>(false);

	// Track which buttons should be visible
	const [showButtons, setShowButtons] = useState<{
		updateClaim: boolean;
		cancel: boolean;
		approve: boolean;
		reject: boolean;
	}>({
		updateClaim: false,
		cancel: false,
		approve: false,
		reject: false
	});

	const [reviewClaimDetails, setReviewClaimDetails] = useState<ReviewClaimDetailsType>({
		status: claimInfo?.claim_status.toUpperCase() ?? "",
		source: claimInfo?.source ?? "",
		payment_mode: initialPaymentMode,
		submission_datetime: moment(claimInfo?.created_on).format(DateTimeFormats.USE_AM_PM),
		username: username ?? "",
		agent_id: user_id ?? "",
		receipt_number: claimInfo?.receipt_number ?? "",
		receipt_datetime: moment(claimInfo?.receipt_datetime).format(DateTimeFormats.USE_SECONDS),
		cr_card_number: claimInfo?.cr_card_number ?? "",
		purchase_amount: claimInfo?.purchase_amount?.toString() ?? "",
		net_amount: claimInfo?.net_amount?.toString() ?? "",
		receipt_imgurl: claimInfo?.receipt_imgurl ?? ""
	});

	const [disableFields, setDisableFields] = useState<DisabledFieldsType>({
		receipt_number: true,
		receipt_datetime: true,
		cr_card_number: true,
		outlet_code: true,
		purchase_amount: true,
		net_amount: true,
		payment_mode: true,
		receipt_imgurl: true,
		approve_btn: true,
		reject_btn: true,
		spl_approve: true
	});

	const [isAgent, setIsAgent] = useState<boolean>(true);
	const [approvalLimit, setApprovalLimit] = useState<number>();
	const [claimApprovalLimit, setClaimApprovalLimit] = useState<number>(0);

	const [outletCode, setOutletCode] = useState<string>(claimInfo?.outlet_code);
	const [outletName, setOutletName] = useState<string>(claimInfo?.outlet_name);
	const [paymentMode, setPaymentMode] = useState<string>(initialPaymentMode);
	const [storesDataList, setStoresDataList] = useState<Array<OutletItem>>([]);
	const [searchValue, setSearchValue] = useState<string>("");

	const [filledDataValidation, setFilledDataValidation] = useState<FilledDataValidationType>({});

	const [terminal, setTerminal] = useState<string>("");
	const [dateVal, setDateVal] = useState<string>("");
	const [isOutletInactive, setIsOutletInactive] = useState<boolean>(false);

	// Fetch claim permissions when component loads
	useEffect(() => {
		if (claimInfo?.retro_claim_id) {
			dispatch(getRetroClaimPermissions({ retro_claim_id: claimInfo.retro_claim_id }))
				.unwrap()
				.catch(() => {
					CountDownModal({
						timer: 3,
						title: "Failed to fetch claim permissions",
						errorIcon: true
					});
				});
		}
	}, [dispatch, claimInfo?.retro_claim_id]);

	// Update disable fields based on permissions
	useEffect(() => {
		if (claimPermissions && "can_modify" in claimPermissions && "can_approve" in claimPermissions && "can_reject" in claimPermissions) {
			// Update field disable states based on can_modify permission
			// Disable approve button if outlet is inactive, even if user has approve permission
			setDisableFields((prevState) => ({
				...prevState,
				receipt_number: !claimPermissions.can_modify,
				receipt_datetime: !claimPermissions.can_modify,
				cr_card_number: !claimPermissions.can_modify,
				outlet_code: !claimPermissions.can_modify,
				purchase_amount: !claimPermissions.can_modify,
				net_amount: !claimPermissions.can_modify,
				payment_mode: !claimPermissions.can_modify,
				receipt_imgurl: !claimPermissions.can_modify,
				approve_btn: !claimPermissions.can_approve || isOutletInactive,
				reject_btn: !claimPermissions.can_reject,
				spl_approve: !claimPermissions.can_approve
			}));

			// Initial button visibility based on permissions (when user hasn't made edits)
			if (!hasUserMadeEdits) {
				const showUpdateCancel = claimPermissions.can_modify && !claimPermissions.can_approve && !claimPermissions.can_reject;
				setShowButtons({
					updateClaim: showUpdateCancel,
					cancel: showUpdateCancel,
					approve: claimPermissions.can_approve,
					reject: claimPermissions.can_reject
				});
			}
		}
	}, [claimPermissions, isOutletInactive, hasUserMadeEdits]);

	// Update button visibility when user makes edits: show update/cancel, hide approve/reject
	useEffect(() => {
		if (hasUserMadeEdits && claimPermissions?.can_modify) {
			setShowButtons({
				updateClaim: true,
				cancel: true,
				approve: false,
				reject: false
			});
		}
	}, [hasUserMadeEdits, claimPermissions]);

	const configurationInfo = useCallback(async () => {
		const { data } = await dispatch(getConfigData({ config_dtl_code: "4003" })).unwrap();
		setApprovalLimit(data?.[0]?.config_dtl_val3);

		const retroClaimApprovalDetails = userPermissionList?.filter((item: any) => item.perm_group_code === "5004");
		setClaimApprovalLimit(retroClaimApprovalDetails?.[0]?.perm_val2);
	}, [dispatch, approvalLimit, claimApprovalLimit, getConfigData, userInfo, userPermissionList, setApprovalLimit, setClaimApprovalLimit, setIsAgent]);

	useEffect(() => {
		if (approvalLimit && claimApprovalLimit && claimApprovalLimit > approvalLimit) {
			setIsAgent(false);
		}
	}, [claimApprovalLimit, approvalLimit]);

	const getStoresInfo = useCallback(async () => {
		try {
			const { data } = await dispatch(getStoreList({})).unwrap();

			if (data?.length) {
				setStoresDataList(data);
			}
		} catch (error) {
			const err = error as any;
			if (err?.response?.data?.errors && err?.response?.data?.errors?.length > 0) {
				CountDownModal({
					timer: 3,
					title: Constants.CANNOT_SEND_REQUEST,
					errorIcon: true,
					onTimeComplete: goBack
				});
			}
		}
	}, [dispatch, getStoreList, setStoresDataList]);

	useEffect(() => {
		configurationInfo();
		getStoresInfo();
	}, []);

	useEffect(() => {
		if (
			(claimApprovalLimit && claimInfo?.claim_status === Constants.CLAIM_STATUS_PENDING) ||
			(claimApprovalLimit && claimInfo?.claim_status === Constants.CLAIM_STATUS_PENDING_APPROVAL)
		) {
			setDisableFields({});
			if (isAgent) {
				if (approvalLimit && parseFloat(reviewClaimDetails?.net_amount) > approvalLimit) {
					setDisableFields({
						approve_btn: true
					});
				}
			} else {
				setDisableFields({ spl_approve: true });
				if (claimApprovalLimit && parseFloat(reviewClaimDetails?.net_amount) > claimApprovalLimit) {
					setDisableFields({
						spl_approve: true,
						approve_btn: true
					});
				}
			}
		}
	}, [isAgent, claimInfo, reviewClaimDetails, approvalLimit, claimApprovalLimit]);

	const [reviewClaimDetailsToBeValidated, setReviewClaimDetailsToBeValidated] = useState({
		receipt_number: "",
		cr_card_number: "",
		purchase_amount: "",
		net_amount: "",
		payment_mode: "",
		receipt_datetime: "",
		receipt_imgurl: ""
	});

	useEffect(() => {
		setReviewClaimDetailsToBeValidated({
			receipt_number: reviewClaimDetails.receipt_number,
			cr_card_number: reviewClaimDetails.cr_card_number,
			purchase_amount: reviewClaimDetails.purchase_amount,
			net_amount: reviewClaimDetails.net_amount,
			payment_mode: reviewClaimDetails.payment_mode,
			receipt_datetime: reviewClaimDetails.receipt_datetime,
			receipt_imgurl: reviewClaimDetails.receipt_imgurl
		});
	}, [reviewClaimDetails]);

	const [fieldErrors, setFieldErrors] = useState<ReviewClaimFieldErrors>(initialErrorState);

	const [detailsFilled, setDetailsFilled] = useState<RetroClaimType>({
		retro_claim_id: claimInfo?.retro_claim_id,
		receipt_number: reviewClaimDetails.receipt_number,
		cr_card_number: reviewClaimDetails.cr_card_number,
		receipt_datetime: moment(reviewClaimDetails.receipt_datetime).format(DateTimeFormats.USE_SECONDS),
		purchase_amount: parseFloat(reviewClaimDetails.purchase_amount),
		net_amount: parseFloat(reviewClaimDetails.net_amount),
		source: reviewClaimDetails.source,
		payment_mode: reviewClaimDetails.payment_mode,
		receipt_img_base64: reviewClaimDetails.receipt_imgurl,
		outlet_code: outletCode
	});

	// Check all required fields are entered
	const isNextEnabled = useMemo(() => {
		const values = Object.values(reviewClaimDetailsToBeValidated);
		const errors = Object.values(fieldErrors);

		for (let i = 0; i < values?.length; i++) {
			if (!values[i]) {
				return false;
			}
		}

		for (let i = 0; i < errors?.length; i++) {
			if (errors[i] === true) {
				return false;
			}
		}

		if (!outletCode?.length) {
			return false;
		}

		return true;
	}, [fieldErrors, reviewClaimDetailsToBeValidated, outletCode]);

	// Prepare payload once we have all the details
	// receipt_datetime needs to be converted to UTC before calling API
	useEffect(() => {
		const apiPayload = {
			retro_claim_id: claimInfo?.retro_claim_id,
			receipt_number: reviewClaimDetails.receipt_number,
			cr_card_number: reviewClaimDetails.cr_card_number,
			receipt_datetime: reviewClaimDetails.receipt_datetime,
			purchase_amount: parseFloat(reviewClaimDetails.purchase_amount),
			net_amount: parseFloat(reviewClaimDetails.net_amount),
			outlet_code: outletCode,
			source: reviewClaimDetails.source,
			payment_mode: formatPaymentModeForApi(reviewClaimDetails.payment_mode)
		};
		if (isNextEnabled) {
			setDetailsFilled({
				...apiPayload,
				receipt_img_base64: reviewClaimDetails.receipt_imgurl?.length ? reviewClaimDetails.receipt_imgurl : claimInfo?.receipt_imgurl,
				status: reviewClaimDetails.status
			});
		}
	}, [isNextEnabled, reviewClaimDetails, outletCode]);

	// Panel Headers
	const generateHeader = useMemo(() => {
		return <p className={styles?.panel_header}>{isAgent ? REVIEW_RETRO_CLAIM.MESSAGE_APPROVER : REVIEW_RETRO_CLAIM.MESSAGE_AGENT}</p>;
	}, [isAgent]);

	// Column Headers
	const generateCols = useMemo(() => {
		return [
			{
				dataIndex: REVIEW_RETRO_CLAIM.COLUMN_HEADERS.REVIEW_DATETIME.dataIndex,
				title: <p className={styles?.headerContentText}>{REVIEW_RETRO_CLAIM.COLUMN_HEADERS.REVIEW_DATETIME.title}</p>,
				width: 200
			},
			{
				dataIndex: REVIEW_RETRO_CLAIM.COLUMN_HEADERS.MESSAGE.dataIndex,
				title: <p className={styles?.headerContentText}>{REVIEW_RETRO_CLAIM.COLUMN_HEADERS.MESSAGE.title}</p>,
				width: 350
			},
			{
				dataIndex: REVIEW_RETRO_CLAIM.COLUMN_HEADERS.AGENT_ID.dataIndex,
				title: <p className={styles?.headerContentText}>{REVIEW_RETRO_CLAIM.COLUMN_HEADERS.AGENT_ID.title}</p>,
				width: 125
			},
			{
				dataIndex: REVIEW_RETRO_CLAIM.COLUMN_HEADERS.ATTACHMENTS.dataIndex,
				title: <p className={styles?.headerContentText}>{REVIEW_RETRO_CLAIM.COLUMN_HEADERS.ATTACHMENTS.title}</p>,
				width: 275
			}
		];
	}, [styles]);

	const onFailure = useCallback(() => {
		CountDownModal({
			timer: 3,
			title: Constants.NO_DATA_FOUND,
			errorIcon: true
		});
	}, [CountDownModal]);

	const handleDownload = useCallback(
		async (file_url: string) => {
			if (file_url) {
				try {
					await downloadFile(file_url);
				} catch (error) {
					onFailure();
				}
			} else {
				onFailure();
			}
		},
		[downloadFile, onFailure]
	);

	// New Panel Headers for Modification History
	const generateModificationHistoryHeader = useMemo(() => {
		return <p className={styles?.panel_header}>Modification History</p>;
	}, []);

	// Column Headers for Modification History
	const generateModificationHistoryCols = useMemo(() => {
		return [
			{
				dataIndex: "modified_by",
				title: <p className={styles?.headerContentText}>Modified By</p>,
				width: 200
			},
			{
				dataIndex: "modified_on",
				title: <p className={styles?.headerContentText}>Modified On</p>,
				width: 200
			},
			{
				dataIndex: "last_updated_by",
				title: <p className={styles?.headerContentText}>Last Updated By</p>,
				width: 200
			}
		];
	}, [styles]);

	// Table Data for Modification History
	const generateModificationHistoryData = useMemo(() => {
		if (!claimInfo) return [];

		// Check if we have history data in the new format
		if (claimInfo?.modified_by_history && claimInfo?.modified_on_history) {
			const modifiedByArray = claimInfo.modified_by_history.split(";");
			const modifiedOnArray = claimInfo.modified_on_history.split(";");

			// Create an array of history entries
			return modifiedByArray.map((modifiedBy: string, index: number) => {
				const modifiedOn = modifiedOnArray[index] || "";

				return {
					key: `${claimInfo?.retro_claim_id}-history-${index}`,
					modified_by: <p className={styles?.extraContentText}>{modifiedBy || "-"}</p>,
					modified_on: <p className={styles?.extraContentText}>{modifiedOn ? moment(modifiedOn).add(8, "hours").format(DateTimeFormats.USE_DD_MMM) : "-"}</p>,
					last_updated_by: <p className={styles?.extraContentText}>{modifiedBy || "-"}</p>
				};
			});
		}

		// Fallback to the old format if history data is not available
		return [
			{
				key: `${claimInfo?.retro_claim_id}-history`,
				modified_by: <p className={styles?.extraContentText}>{claimInfo?.modified_by || "-"}</p>,
				modified_on: <p className={styles?.extraContentText}>{claimInfo?.modified_on ? moment(claimInfo?.modified_on).add(8, "hours").format(DateTimeFormats.USE_DD_MMM) : "-"}</p>,
				last_updated_by: <p className={styles?.extraContentText}>{claimInfo?.modified_by || "-"}</p>
			}
		];
	}, [claimInfo, styles]);

	// Table Data
	const generateData = useMemo(() => {
		const documentString = isAgent ? claimInfo?.approver_documents : claimInfo?.agent_documents;
		const reviewDateApprover = claimInfo?.claim_status === REVIEW_RETRO_CLAIM.REJECTED_STATUS ? claimInfo?.rejected_on : claimInfo?.approved_on;
		const agentIDApprover = claimInfo?.claim_status === REVIEW_RETRO_CLAIM.REJECTED_STATUS ? claimInfo?.rejected_by : claimInfo?.approved_by;
		const reviewDateVal = isAgent ? moment(reviewDateApprover).format(DateTimeFormats.USE_DD_MMM) : moment(claimInfo?.verified_on).format(DateTimeFormats.USE_DD_MMM);
		return [
			{
				key: claimInfo?.retro_claim_id,
				review_dt: <p className={styles?.extraContentText}>{reviewDateVal !== DateTimeFormats.MOMENT_INVALID_DATE ? reviewDateVal : ""}</p>,
				message: <p className={styles?.extraContentText}>{isAgent ? claimInfo?.approver_remarks : claimInfo?.agent_remarks}</p>,
				agent_id: <p className={styles?.extraContentText}>{isAgent ? agentIDApprover : claimInfo?.verified_by}</p>,
				attachments: (
					<div>
						{documentString?.split("|")?.map((document, index) => {
							return (
								<Button
									onClick={() => handleDownload(document)}
									key={`${document}-${index}`}
									className={styles?.download_button}
									icon={<span className="inline">{getIcon({ icon: "download" })}</span>}
								>
									<span className={styles?.download_button_text}>{getFilename(document)}</span>
								</Button>
							);
						})}
					</div>
				)
			}
		];
	}, [isAgent, claimInfo, handleDownload]);

	// Modified handler for updating claim details entered
	const handleReviewClaimDetailsChange = useCallback(
		({ id, value }: { id: string; value: string }) => {
			const newData = {
				...reviewClaimDetails,
				[id]: value
			};
			setReviewClaimDetails(newData);

			// Set hasUserMadeEdits to true when user makes any change
			if (!hasUserMadeEdits) {
				setHasUserMadeEdits(true);
			}
		},
		[reviewClaimDetails, hasUserMadeEdits, setReviewClaimDetails, setHasUserMadeEdits]
	);

	const handleInputChange = (e: any) => {
		handleReviewClaimDetailsChange({
			id: e?.target?.id,
			value: e?.target?.value
		});
	};

	const handlePaymentMode = useCallback(
		(value: string) => {
			setPaymentMode(value);
			handleReviewClaimDetailsChange({ id: "payment_mode", value });
		},
		[handleReviewClaimDetailsChange]
	);

	// Handler for Outlet Select
	const handleOutletSelect = (value: string) => {
		const optionValueArray = value.split("*");
		setOutletName(optionValueArray[0]);
		setOutletCode(optionValueArray[1]);

		// Check if selected outlet is active
		const selectedOutlet = storesDataList?.find((store) => store?.outlet_code === optionValueArray[1]);
		if (selectedOutlet) {
			setIsOutletInactive(false);
		} else {
			setIsOutletInactive(true);
		}

		// Set hasUserMadeEdits to true when user selects an outlet
		if (!hasUserMadeEdits) {
			setHasUserMadeEdits(true);
		}
	};

	const handleOutletSearch = (value: string) => {
		setSearchValue(value);
	};

	// Handle Receipt Date changes
	const handleOnDateChange = useCallback(
		(_value: DatePickerProps["value"], dateString: string) => {
			const receipt_dt = moment(dateString, [DateTimeFormats.USE_AM_PM]).format(DateTimeFormats.USE_AM_PM);
			setDateVal(receipt_dt);

			// Set hasUserMadeEdits to true when user changes date
			if (!hasUserMadeEdits) {
				setHasUserMadeEdits(true);
			}
		},
		[dateVal, hasUserMadeEdits, setHasUserMadeEdits]
	);

	const handleOnDateOk = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		(_value: DatePickerProps["value"]) => {
			const newData = {
				...reviewClaimDetails,
				receipt_datetime: moment(dateVal, [DateTimeFormats.USE_AM_PM]).format(DateTimeFormats.USE_SECONDS)
			};
			setReviewClaimDetails(newData);
		},
		[reviewClaimDetails, setReviewClaimDetails]
	);

	// useEffect being used due to a possibility of ANT component onChange and onOk called at the same time
	useEffect(() => {
		if (dateVal !== "") {
			const newData = {
				...reviewClaimDetails,
				receipt_datetime: moment(dateVal, [DateTimeFormats.USE_AM_PM]).format(DateTimeFormats.USE_SECONDS)
			};
			setReviewClaimDetails(newData);
		}
	}, [dateVal]);

	// Handle Image Update
	const handleUpload = useCallback(
		(url: string) => {
			const newData = {
				...reviewClaimDetails,
				receipt_imgurl: url
			};
			setReviewClaimDetails(newData);

			// Set hasUserMadeEdits to true when user uploads an image
			if (!hasUserMadeEdits) {
				setHasUserMadeEdits(true);
			}
		},
		[reviewClaimDetails, hasUserMadeEdits, setReviewClaimDetails, setHasUserMadeEdits]
	);

	const goBack = useCallback(() => {
		navigate(`${Routes.RETRO_CLAIM}`);
	}, [navigate]);

	const validateFields = useCallback(
		(submit: boolean, keyForReValidation?: string) => {
			// reset error state before validations
			setFieldErrors(initialErrorState);

			const errors: Array<{ field: string | undefined }> = [];

			fieldsToValidate.forEach((field: { key: string; validator: (args: string) => boolean }) => {
				const key = field.key as keyof ReviewClaimFieldErrors;
				const value = reviewClaimDetails[key];
				if (value || submit || (!value && keyForReValidation)) {
					const isValid = field.validator(value);

					if (!isValid) {
						errors.push({ field: field.key });
					}
				}
			});

			if (errors.length) {
				const errs: ReviewClaimFieldErrors = {};
				errors.forEach((err) => {
					errs[err.field as keyof ReviewClaimFieldErrors] = true;
				});
				setFieldErrors(errs);
				return;
			}

			return true;
		},
		[reviewClaimDetails, fieldErrors, setFieldErrors]
	);

	const filledDataValidationHandler = (errorDetailsArray: string[]) => {
		if (errorDetailsArray.length > 1) {
			setFilledDataValidation({ receipt_datetime: true, net_amount: true });
		} else {
			errorDetailsArray.forEach((errorDetailItem: string) => {
				if (errorDetailItem === Constants.RECEIPT_DATETIME_ERROR) {
					setFilledDataValidation({ receipt_datetime: true });
				}
				if (errorDetailItem === Constants.NET_AMOUNT_ERROR) {
					setFilledDataValidation({ net_amount: true });
				}
			});
		}
	};

	const handleApprove = useCallback(async () => {
		dispatch(retroClaimInfoUpdater(detailsFilled));
		const { data: ConfigDate } = await dispatch(getConfigData({ config_dtl_code: "4004" })).unwrap();
		const errorDetails: Array<string> = await validateReviewClaim(detailsFilled?.receipt_datetime, detailsFilled?.net_amount, ConfigDate?.[0]?.config_dtl_val3);
		if (errorDetails.length) {
			const errorDescription =
				errorDetails.length > 1
					? `1. ${errorDetails[0]}
			2. ${errorDetails[1]}`
					: `1. ${errorDetails[0]}`;
			filledDataValidationHandler(errorDetails);
			Modal({
				closable: true,
				type: "confirm",
				title: REVIEW_RETRO_CLAIM.REVIEW_MODAL_TITLE,
				cancelButton: {
					label: "Go back",
					classes: {
						label: "back_button_label_white",
						buttonInput: "back_button_violet"
					}
				},
				description: errorDescription
			});
		} else {
			// Only navigate to the approve page without making API call
			navigate(`${Routes.RETRO_CLAIM}/${Routes.APPROVE_RETRO_CLAIM}`, {
				state: {
					retroClaimId: claimInfo?.retro_claim_id,
					throughCreateClaim: false,
					outlet: outletName
				}
			});
		}
	}, [navigate, validateReviewClaim, filledDataValidationHandler, getConfigData, detailsFilled, claimInfo, outletName]);

	const handleGoBackToReview = useCallback(async () => {
		navigate(-1);
	}, [navigate]);

	const handleReject = useCallback(async () => {
		dispatch(retroClaimInfoUpdater(detailsFilled));
		// Only navigate to the reject page without making API call
		navigate(`${Routes.RETRO_CLAIM}/${Routes.REJECT_RETRO_CLAIM}`, {
			state: {
				retroClaimId: claimInfo?.retro_claim_id,
				throughCreateClaim: false
			}
		});
	}, [navigate, detailsFilled, claimInfo]);

	const handleTerminalSelect = useCallback(
		(value: string) => {
			setTerminal(() => value);
			setOutletCode("");
			setOutletName("");
			setIsOutletInactive(false);
			form.setFieldsValue({ outlet_code: null });

			// Set hasUserMadeEdits to true when user selects a terminal
			if (!hasUserMadeEdits) {
				setHasUserMadeEdits(true);
			}
		},
		[setTerminal, setOutletCode, setOutletName, form, terminal, hasUserMadeEdits, setHasUserMadeEdits]
	);

	const handleUpdateClaim = useCallback(async () => {
		try {
			dispatch(retroClaimInfoUpdater(detailsFilled));
			const { errors } = await dispatch(
				editRetroClaim(
					reviewClaimDetails.receipt_imgurl === claimInfo?.receipt_imgurl
						? {
								...detailsFilled,
								receipt_img_base64: reviewClaimDetails.receipt_imgurl.split(",")?.[1],
								receipt_datetime: moment(reviewClaimDetails.receipt_datetime).utc().format(DateTimeFormats.USE_SECONDS)
						  }
						: { ...detailsFilled, receipt_datetime: moment(reviewClaimDetails.receipt_datetime).utc().format(DateTimeFormats.USE_SECONDS) }
				)
			).unwrap();

			if (!errors?.length) {
				CountDownModal({
					timer: 3,
					title: "Claim successfully updated!",
					onTimeComplete: () => navigate(`${Routes.RETRO_CLAIM}`)
				});
			}
		} catch (error) {
			const err = error as any;
			if (err?.response?.data?.errors && err?.response?.data?.errors?.length > 0) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: err?.response?.data?.errors?.[0]?.message,
					onTimeComplete: goBack
				});
			}
		}
	}, [dispatch, navigate, moment, detailsFilled, claimInfo, reviewClaimDetails, goBack]);

	const { outletsList, terminalsList } = useMemo(() => {
		const terminalsDataList: Array<{ id: string; value: string }> = [];
		const outletsDataList: OutletItem[] = [];

		storesDataList?.forEach((store: OutletItem) => {
			if (store?.terminal && !terminalsDataList?.find(({ id }) => id === store?.terminal)) {
				terminalsDataList.push({ id: store?.terminal, value: store?.terminal });
			}
			/**
			 * Add outlets based on *terminal* selection. If no terminal do not show outlets.
			 */
			if (terminal && store?.outlet_code && !outletsDataList?.find(({ outlet_code }) => outlet_code === store?.outlet_code) && store?.terminal === terminal) {
				outletsDataList.push(store);
			}
		});
		return { outletsList: outletsDataList, terminalsList: terminalsDataList };
	}, [terminal, outletCode, outletName, setTerminal, setOutletCode, setOutletName, storesDataList]);

	// Check if current outlet is inactive and set terminal
	useMemo(() => {
		if (storesDataList?.length > 0) {
			const currentOutlet = storesDataList?.find((store) => store?.outlet_code === claimInfo?.outlet_code);

			// Check if outlet is inactive (not found in active stores list)
			if (claimInfo?.outlet_code && !currentOutlet) {
				setIsOutletInactive(true);
			} else {
				setIsOutletInactive(false);
				// Set terminal if outlet is active and terminal is not set
				if (!terminal && currentOutlet?.terminal) {
					setTerminal(currentOutlet.terminal);
					form.setFieldsValue({ terminal: currentOutlet.terminal });
				}
			}
		}
	}, [terminal, storesDataList, claimInfo?.outlet_code, form]);

	useEffect(() => {
		const normalized = normalizePaymentMode(claimInfo?.payment_mode);

		if (normalized) {
			setPaymentMode(normalized);
			form.setFieldsValue({ payment_mode: normalized });
			setReviewClaimDetails((prevState) => ({
				...prevState,
				payment_mode: normalized
			}));
		}
	}, [claimInfo?.payment_mode, form]);

	return {
		reviewClaimDetails,
		disableFields,
		isNextEnabled,
		fieldErrors,
		storesDataList,
		outletCode,
		paymentMode,
		loading: dataLoading === 1 || configDataLoading === 1,
		generateHeader,
		generateCols,
		generateData,
		generateModificationHistoryHeader,
		generateModificationHistoryCols,
		generateModificationHistoryData,
		filledDataValidation,
		outletName,
		form,
		terminal,
		terminalsList,
		filteredOutletsList: outletsList,
		handleTerminalSelect,
		searchValue,
		handleOutletSearch,
		handleOutletSelect,
		handleInputChange,
		handlePaymentMode,
		handleOnDateChange,
		handleOnDateOk,
		handleUpload,
		validateFields,
		handleApprove,
		handleReject,
		handleGoBackToReview,
		handleUpdateClaim,
		goBack,
		showButtons,
		hasUserMadeEdits,
		isOutletInactive,
		claimStatus: claimInfo?.claim_status
	};
}
